import SearchableSelect from '../components/search/SearchableSelect';
import CollectionSearch from '../components/search/CollectionSearch';
import { filterRecords } from '../utils/search';
import React, { useState, useEffect } from 'react';
import {
  Typography, Container, Box, Button, CircularProgress, Alert, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, MenuItem, Select, InputLabel, FormControl, useMediaQuery, useTheme, Card, CardContent, Stack, Chip, Box as MuiBox, FormControlLabel, Switch, IconButton, Tooltip
} from '@mui/material';
import { NoteAlt, NoteAltOutlined, PauseCircle, PlayCircle } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { getAutopayments, createAutopayment, updateAutopayment, deleteAutopayment, toggleAutopayment, getAccounts, getCategories } from '../services/api';
import { newRequestKey, requestStorageKey } from '../utils/intake';
import { occurrenceDate, PaymentTypeField, TransferDestinationFields, RecordOccurrenceField } from '../components/RecurringTransferFields';

function AutopaymentsPage() {
  const [autopayments, setAutopayments] = useState([]);
  const [search, setSearch] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);
  const [transferReady, setTransferReady] = useState(false);
  const [creationKey, setCreationKey] = useState('');
  const [pendingCreation, setPendingCreation] = useState(null);
  const [serverDate, setServerDate] = useState(new Date().toISOString().slice(0, 10));
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedAutopayment, setSelectedAutopayment] = useState(null);
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [autopaymentToDelete, setAutopaymentToDelete] = useState(null);

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Show all toggle for desktop table
  const [showAll, setShowAll] = useState(false);

  // Notes modal state
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [notesAutopayment, setNotesAutopayment] = useState(null);
  const [notesText, setNotesText] = useState('');

  const [newAutopaymentData, setNewAutopaymentData] = useState({
    account_id: '',
    category_id: '',
    day_of_month: '',
    amount: '',
    payee: '',
    notes: '',
    frequency: 'monthly',
    month_of_year: '', payment_type: 'payment', to_account_id: '', to_category_id: '', record_now: false
  });

  // Month names for dropdown
  const monthNames = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  // Get max day for selected month (28 for Feb in yearly mode)
  const sourceAccountLabel = newAutopaymentData.payment_type === 'transfer' ? 'From account' : 'Account';
  const sourceCategoryLabel = newAutopaymentData.payment_type === 'transfer' ? 'From category' : 'Category';
  const getMaxDay = () => {
    if (newAutopaymentData.frequency === 'yearly' && newAutopaymentData.month_of_year) {
      const month = parseInt(newAutopaymentData.month_of_year);
      if (month === 2) return 28;
      if ([4, 6, 9, 11].includes(month)) return 30;
    }
    return 31;
  };

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const [autopaymentsRes, accountsRes, categoriesRes] = await Promise.all([
        getAutopayments(),
        getAccounts(),
        getCategories()
      ]);
      const sortedAutopayments = autopaymentsRes.data.sort((a, b) => a.day_of_month - b.day_of_month);
      setAutopayments(sortedAutopayments);
      setTransferReady(autopaymentsRes.headers?.['x-recurring-transfers'] === '1');
      setServerDate(sortedAutopayments[0]?.server_date || new Date(autopaymentsRes.headers?.date || Date.now()).toISOString().slice(0, 10));
      setAccounts(accountsRes.data);
      setCategories(categoriesRes.data);
    } catch (err) {
      console.error("Failed to fetch data", err);
      setError(err.response?.data?.msg || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenModal = () => {
    let pending = null;
    try { pending = JSON.parse(sessionStorage.getItem(requestStorageKey('autopayment-create'))); } catch { /* Invalid local state is not a saved request. */ }
    if (pending?.request_key) {
      setPendingCreation(pending); setCreationKey(pending.request_key);
      setNewAutopaymentData(pending); setIsModalOpen(true);
      setError(`A previous save${pending.occurrence_date ? ` for ${pending.occurrence_date}` : ''} was not confirmed. Retry this same saved request to check or complete it without creating a duplicate.`);
      return;
    }
    setPendingCreation(null); setError(''); setCreationKey(newRequestKey());
    setIsModalOpen(true);
    setNewAutopaymentData({
      account_id: '',
      category_id: '',
      day_of_month: '',
      amount: '',
      payee: '',
      notes: '',
      frequency: 'monthly',
      month_of_year: '', payment_type: 'payment', to_account_id: '', to_category_id: '', record_now: false
    });
  };
  const handleCloseModal = () => { if (!saving) setIsModalOpen(false); };

  const handleOpenEditModal = (autopayment) => {
    setError('');
    setSelectedAutopayment(autopayment);
    setNewAutopaymentData({
      account_id: autopayment.account_id,
      category_id: autopayment.category_id,
      day_of_month: autopayment.day_of_month,
      amount: autopayment.amount,
      payee: autopayment.payee,
      notes: autopayment.notes || '',
      frequency: autopayment.frequency || 'monthly',
      month_of_year: autopayment.month_of_year || '',
      payment_type: autopayment.payment_type || 'payment', to_account_id: autopayment.to_account_id || '',
      to_category_id: autopayment.to_category_id || '', is_active: autopayment.is_active, record_now: false
    });
    setIsEditModalOpen(true);
  };
  const handleCloseEditModal = () => {
    if (saving) return;
    setIsEditModalOpen(false);
    setSelectedAutopayment(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewAutopaymentData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'account_id' ? { category_id: '' } : {}),
      ...(name === 'to_account_id' ? { to_category_id: '' } : {}),
      ...(['payment_type', 'frequency', 'month_of_year', 'day_of_month'].includes(name) ? { record_now: false } : {})
    }));
  };

  const savePayload = () => ({ ...newAutopaymentData,
    ...(newAutopaymentData.record_now ? { occurrence_date: occurrenceDate(newAutopaymentData, serverDate) } : {}) });
  const saveNotice = result => {
    setNotice(result.recorded_now ? 'Saved and recorded this occurrence. The scheduler will not repeat it.'
      : result.already_recorded ? 'Already recorded. No duplicate entries were added.' : 'Automated payment saved.');
    window.dispatchEvent(new Event('accounting:transactions-changed'));
  };

  const handleCreateAutopayment = async () => {
    if (saving) return;
    const pendingKey = requestStorageKey('autopayment-create');
    const payload = pendingCreation || { ...savePayload(), request_key: creationKey };
    if (!transferReady && (payload.payment_type === 'transfer' || payload.record_now)) {
      setError('The server has not confirmed support for this saved request. Refresh after the upgrade; your request is retained and has not been resubmitted.');
      return;
    }
    setSaving(true); setError('');
    try {
      // Freeze the request before submission; a lost response or modal reopen reuses it exactly.
      sessionStorage.setItem(pendingKey, JSON.stringify(payload)); setPendingCreation(payload);
      const result = await createAutopayment(payload);
      sessionStorage.removeItem(pendingKey); setPendingCreation(null);
      saveNotice(result.data); setIsModalOpen(false);
      fetchData();
    } catch (err) {
      console.warn('Automated payment save was not confirmed.');
      if ([400, 404].includes(err.response?.status) || err.response?.data?.safe_to_edit === true) {
        sessionStorage.removeItem(pendingKey); setPendingCreation(null);
      }
      setError(err.response?.data?.msg || err.response?.data?.message || 'Save could not be confirmed. Retry with these same details or refresh to check acceptance.');
    } finally { setSaving(false); }
  };

  const handleUpdateAutopayment = async () => {
    if (saving) return;
    setSaving(true); setError('');
    try {
      const result = await updateAutopayment(selectedAutopayment.id, savePayload());
      saveNotice(result.data); setIsEditModalOpen(false); setSelectedAutopayment(null);
      fetchData();
    } catch (err) {
      console.warn('Automated payment update was not confirmed.');
      setError(err.response?.data?.msg || err.response?.data?.message || 'Save could not be confirmed. Refresh to check acceptance before retrying.');
    } finally { setSaving(false); }
  };

  const handleDeleteAutopayment = (id) => {
    setAutopaymentToDelete(id);
    setOpenConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteAutopayment(autopaymentToDelete);
      fetchData();
      setOpenConfirmDelete(false);
      setAutopaymentToDelete(null);
    } catch (err) {
      console.error("Failed to delete autopayment", err);
      setError(err.response?.data?.msg || 'Failed to delete autopayment');
    }
  };

  const handleCancelDelete = () => {
    setOpenConfirmDelete(false);
    setAutopaymentToDelete(null);
  };

  // Notes modal handlers
  const handleOpenNotesModal = (autopayment) => {
    setNotesAutopayment(autopayment);
    setNotesText(autopayment.notes || '');
    setNotesModalOpen(true);
  };

  const handleCloseNotesModal = () => {
    setNotesModalOpen(false);
    setNotesAutopayment(null);
    setNotesText('');
  };

  const handleSaveNotes = async () => {
    try {
      await updateAutopayment(notesAutopayment.id, { notes: notesText });
      fetchData();
      handleCloseNotesModal();
    } catch (err) {
      console.error("Failed to update notes", err);
      setError(err.response?.data?.msg || 'Failed to update notes');
    }
  };

  // Toggle pause/resume for autopayment
  const handleToggleAutopayment = async (id) => {
    try {
      await toggleAutopayment(id);
      fetchData();
    } catch (err) {
      console.error("Failed to toggle autopayment", err);
      setError(err.response?.data?.msg || 'Failed to toggle autopayment');
    }
  };

  // Helper to get month name
  const getMonthName = (monthNum) => {
    const month = monthNames.find(m => m.value === monthNum);
    return month ? month.label : '';
  };

  // Mobile autopayment card component
const MobileAutopaymentCard = ({ autopayment }) => {
  const isYearly = autopayment.frequency === 'yearly';
  const isPaused = !autopayment.is_active;
  const scheduleLabel = isYearly
    ? `${getMonthName(autopayment.month_of_year)} ${autopayment.day_of_month}`
    : `Day ${autopayment.day_of_month}`;

  return (
    <Card sx={{ mb: 2, opacity: isPaused ? 0.6 : 1, bgcolor: isPaused ? 'action.hover' : 'background.paper' }}>
      <CardContent>
        <Stack spacing={1.5}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ textDecoration: isPaused ? 'line-through' : 'none' }}>
                {autopayment.payee || 'No Payee'}
              </Typography>
              {isPaused && (
                <Chip label="Paused" size="small" color="warning" />
              )}
            </Box>
            <Typography variant="h6" color={isPaused ? "text.secondary" : "primary"} fontWeight="bold">
              ${autopayment.amount}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={autopayment.account_name}
              size="small"
              variant="outlined"
            />
            <Chip
              label={autopayment.category_name}
              size="small"
            />
            <Chip
              label={scheduleLabel}
              size="small"
              color="secondary"
              variant="outlined"
            />
            {isYearly && (
              <Chip
                label="Yearly"
                size="small"
                color="info"
              />
            )}
          </Box>

          {autopayment.payment_type === 'transfer' && <Alert severity="info" icon={false}>
            Transfer to {autopayment.to_account_name} → {autopayment.to_category_name}
          </Alert>}
          <Typography variant="caption" color="text.secondary">
            Created: {new Date(autopayment.created_at).toLocaleDateString()}
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Tooltip title={isPaused ? "Resume" : "Pause"}>
              <IconButton
                size="small"
                onClick={() => handleToggleAutopayment(autopayment.id)}
                color={isPaused ? "success" : "warning"}
                sx={{ border: 1, borderColor: isPaused ? 'success.main' : 'warning.main' }}
              >
                {isPaused ? <PlayCircle /> : <PauseCircle />}
              </IconButton>
            </Tooltip>
            <IconButton
              size="small"
              onClick={() => handleOpenNotesModal(autopayment)}
              color={autopayment.notes ? "primary" : "default"}
              sx={{ border: 1, borderColor: autopayment.notes ? 'primary.main' : 'grey.300' }}
            >
              {autopayment.notes ? <NoteAlt /> : <NoteAltOutlined />}
            </IconButton>
            <Button
              variant="contained"
              size="small"
              onClick={() => handleOpenEditModal(autopayment)}
              sx={{ flex: 1, minHeight: 40 }}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => handleDeleteAutopayment(autopayment.id)}
              sx={{ flex: 1, minHeight: 40 }}
            >
              Delete
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.row.is_active ? "Active" : "Paused"}
          size="small"
          color={params.row.is_active ? "success" : "warning"}
          variant={params.row.is_active ? "filled" : "outlined"}
        />
      )
    },
    { field: 'account_name', headerName: 'Account', width: 130 },
    { field: 'category_name', headerName: 'Category', width: 130 },
    { field: 'payment_type', headerName: 'Type', width: 100,
      valueGetter: (value) => value === 'transfer' ? 'Transfer' : 'Payment' },
    { field: 'destination', headerName: 'Transfer destination', width: 230,
      valueGetter: (value, row) => row.payment_type === 'transfer' ? `${row.to_account_name} / ${row.to_category_name}` : '' },
    {
      field: 'schedule',
      headerName: 'Schedule',
      width: 150,
      valueGetter: (value, row) => {
        if (row.frequency === 'yearly') {
          return `${getMonthName(row.month_of_year)} ${row.day_of_month} (Yearly)`;
        }
        return `Day ${row.day_of_month} (Monthly)`;
      }
    },
    { field: 'amount', headerName: 'Amount', width: 100 },
    { field: 'payee', headerName: 'Payee', width: 150 },
    { field: 'created_at', headerName: 'Created At', width: 160,
  valueFormatter: (value) => {
    if (!value) return 'N/A';
    // Handle ISO timestamp format: "2025-06-29T17:44:52.367471"
    const date = new Date(value);
    return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleString();
  }
},
    {
      field: 'actions',
      headerName: 'Actions',
      width: 220,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title={params.row.is_active ? "Pause" : "Resume"}>
            <IconButton
              size="small"
              onClick={() => handleToggleAutopayment(params.row.id)}
              color={params.row.is_active ? "warning" : "success"}
            >
              {params.row.is_active ? <PauseCircle fontSize="small" /> : <PlayCircle fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title={params.row.notes ? "View/Edit Notes" : "Add Notes"}>
            <IconButton
              size="small"
              onClick={() => handleOpenNotesModal(params.row)}
              color={params.row.notes ? "primary" : "default"}
            >
              {params.row.notes ? <NoteAlt fontSize="small" /> : <NoteAltOutlined fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Button onClick={() => handleOpenEditModal(params.row)} size="small">Edit</Button>
          <Button onClick={() => handleDeleteAutopayment(params.row.id)} size="small" color="error">Delete</Button>
        </Box>
      ),
    },
  ];

  const visibleAutopayments = filterRecords(autopayments, search, payment => [
    payment.payee, payment.account_name, payment.category_name, payment.notes, payment.frequency, Number(payment.amount),
    `Day ${payment.day_of_month}`, payment.frequency === 'yearly' ? getMonthName(payment.month_of_year) : '',
    payment.is_active ? 'active' : 'paused',
    payment.payment_type || 'payment', payment.to_account_name, payment.to_category_name,
  ]);

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container>
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Automated Payments
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}

        <Button 
          variant="contained" 
          onClick={handleOpenModal} 
          sx={{ 
            mb: 2,
            width: { xs: '100%', sm: 'auto' },
            py: { xs: 1.5, md: 1 }
          }}
        >
          Add New Autopayment
        </Button>

        <CollectionSearch label="Search automated payments" value={search} onChange={setSearch}
          total={autopayments.length} shown={visibleAutopayments.length} />
        {isMobile ? (
  // Mobile view - cards
  <Box sx={{ mt: 2 }}>
    {visibleAutopayments.length > 0 ? (
      visibleAutopayments.map((autopayment) => (
        <MobileAutopaymentCard 
          key={autopayment.id} 
          autopayment={autopayment} 
        />
      ))
    ) : (
      <Card>
        <CardContent>
          <Typography color="text.secondary" align="center">
            {search ? 'No matching automated payments' : 'No autopayments configured yet'}
          </Typography>
        </CardContent>
      </Card>
    )}
  </Box>
) : (
  // Desktop view - DataGrid
  <>
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
      <FormControlLabel
        control={
          <Switch
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
            size="small"
          />
        }
        label="Show all"
      />
    </Box>
    <div style={{ height: showAll ? 'auto' : 400, width: '100%' }}>
      <DataGrid
        rows={visibleAutopayments}
        columns={columns}
        pageSize={showAll ? 100 : 5}
        rowsPerPageOptions={showAll ? [100] : [5]}
        disableSelectionOnClick
        getRowId={(row) => row.id}
        autoHeight={showAll}
      />
    </div>
  </>
)}

        {/* Add Autopayment Modal */}
        <Dialog
  open={isModalOpen}
  onClose={handleCloseModal}
  maxWidth="sm"
  fullWidth
  sx={{
    '& .MuiDialog-paper': {
      width: { xs: '95%', sm: '80%', md: '60%' },
      m: { xs: 2, sm: 3 }
    }
  }}
>
          <DialogTitle>Add New Autopayment</DialogTitle>
          <DialogContent>
            <Box component="fieldset" disabled={saving || !!pendingCreation} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
            {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
            <DialogContentText>
              Fill in the details for the new automated payment.
            </DialogContentText>
            {transferReady && <PaymentTypeField data={newAutopaymentData} onChange={handleChange} />}
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>{sourceAccountLabel}</InputLabel>
              <SearchableSelect
                name="account_id"
                value={newAutopaymentData.account_id}
                onChange={handleChange}
                label={sourceAccountLabel}
              >
                {accounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.name}
                  </MenuItem>
                ))}
              </SearchableSelect>
            </FormControl>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>{sourceCategoryLabel}</InputLabel>
              <SearchableSelect
                name="category_id"
                value={newAutopaymentData.category_id}
                onChange={handleChange}
                label={sourceCategoryLabel}
              >
                {categories.filter(cat => cat.account_id === newAutopaymentData.account_id).map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </SearchableSelect>
            </FormControl>
            <TransferDestinationFields data={newAutopaymentData} accounts={accounts} categories={categories} onChange={handleChange} />
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Frequency</InputLabel>
              <Select
                name="frequency"
                value={newAutopaymentData.frequency}
                onChange={handleChange}
                label="Frequency"
              >
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </Select>
            </FormControl>
            {newAutopaymentData.frequency === 'yearly' && (
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Month</InputLabel>
                <Select
                  name="month_of_year"
                  value={newAutopaymentData.month_of_year}
                  onChange={handleChange}
                  label="Month"
                >
                  {monthNames.map((month) => (
                    <MenuItem key={month.value} value={month.value}>
                      {month.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              margin="dense"
              name="day_of_month"
              label={`Day of Month (1-${getMaxDay()})`}
              type="number"
              fullWidth
              value={newAutopaymentData.day_of_month}
              onChange={handleChange}
              inputProps={{ min: 1, max: getMaxDay() }}
            />
            <TextField
              margin="dense"
              name="amount"
              label="Amount"
              type="number"
              fullWidth
              value={newAutopaymentData.amount}
              onChange={handleChange}
            />
            <TextField
              margin="dense"
              name="payee"
              label={newAutopaymentData.payment_type === 'transfer' ? 'Transfer description (Optional)' : 'Payee (Optional)'}
              type="text"
              fullWidth
              value={newAutopaymentData.payee}
              onChange={handleChange}
            />
            <TextField
              margin="dense"
              name="notes"
              label="Notes (Optional)"
              type="text"
              fullWidth
              multiline
              rows={2}
              value={newAutopaymentData.notes}
              onChange={handleChange}
            />
            {transferReady && <RecordOccurrenceField data={newAutopaymentData}
              serverDate={pendingCreation?.occurrence_date || serverDate} onChange={handleChange} />}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button disabled={saving} onClick={handleCloseModal}>Cancel</Button>
            <Button disabled={saving} onClick={handleCreateAutopayment}>{pendingCreation ? 'Retry saved request' : newAutopaymentData.record_now ? 'Save and record occurrence' : 'Add Autopayment'}</Button>
          </DialogActions>
        </Dialog>

        {/* Edit Autopayment Modal */}
        <Dialog
  open={isEditModalOpen}
  onClose={handleCloseEditModal}
  maxWidth="sm"
  fullWidth
  sx={{
    '& .MuiDialog-paper': {
      width: { xs: '95%', sm: '80%', md: '60%' },
      m: { xs: 2, sm: 3 }
    }
  }}
>
          <DialogTitle>Edit Autopayment</DialogTitle>
          <DialogContent>
            <Box component="fieldset" disabled={saving} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
            {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
            <DialogContentText>
              Update the details for this automated payment.
            </DialogContentText>
            {transferReady && <PaymentTypeField data={newAutopaymentData} onChange={handleChange} />}
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>{sourceAccountLabel}</InputLabel>
              <SearchableSelect
                name="account_id"
                value={newAutopaymentData.account_id}
                onChange={handleChange}
                label={sourceAccountLabel}
              >
                {accounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.name}
                  </MenuItem>
                ))}
              </SearchableSelect>
            </FormControl>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>{sourceCategoryLabel}</InputLabel>
              <SearchableSelect
                name="category_id"
                value={newAutopaymentData.category_id}
                onChange={handleChange}
                label={sourceCategoryLabel}
              >
                {categories.filter(cat => cat.account_id === newAutopaymentData.account_id).map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </SearchableSelect>
            </FormControl>
            <TransferDestinationFields data={newAutopaymentData} accounts={accounts} categories={categories} onChange={handleChange} />
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Frequency</InputLabel>
              <Select
                name="frequency"
                value={newAutopaymentData.frequency}
                onChange={handleChange}
                label="Frequency"
              >
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </Select>
            </FormControl>
            {newAutopaymentData.frequency === 'yearly' && (
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Month</InputLabel>
                <Select
                  name="month_of_year"
                  value={newAutopaymentData.month_of_year}
                  onChange={handleChange}
                  label="Month"
                >
                  {monthNames.map((month) => (
                    <MenuItem key={month.value} value={month.value}>
                      {month.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              margin="dense"
              name="day_of_month"
              label={`Day of Month (1-${getMaxDay()})`}
              type="number"
              fullWidth
              value={newAutopaymentData.day_of_month}
              onChange={handleChange}
              inputProps={{ min: 1, max: getMaxDay() }}
            />
            <TextField
              margin="dense"
              name="amount"
              label="Amount"
              type="number"
              fullWidth
              value={newAutopaymentData.amount}
              onChange={handleChange}
            />
            <TextField
              margin="dense"
              name="payee"
              label={newAutopaymentData.payment_type === 'transfer' ? 'Transfer description (Optional)' : 'Payee (Optional)'}
              type="text"
              fullWidth
              value={newAutopaymentData.payee}
              onChange={handleChange}
            />
            <TextField
              margin="dense"
              name="notes"
              label="Notes (Optional)"
              type="text"
              fullWidth
              multiline
              rows={2}
              value={newAutopaymentData.notes}
              onChange={handleChange}
            />
            {transferReady && <RecordOccurrenceField data={newAutopaymentData} serverDate={serverDate}
              existing={selectedAutopayment?.current_occurrence} onChange={handleChange} />}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button disabled={saving} onClick={handleCloseEditModal}>Cancel</Button>
            <Button disabled={saving} onClick={handleUpdateAutopayment}>{newAutopaymentData.record_now ? 'Save and record occurrence' : 'Update Autopayment'}</Button>
          </DialogActions>
        </Dialog>

        {/* Confirm Delete Dialog */}
        <Dialog
          open={openConfirmDelete}
          onClose={handleCancelDelete}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {"Confirm Deletion"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure you want to delete this automated payment? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete}>Cancel</Button>
            <Button onClick={handleConfirmDelete} autoFocus color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Notes Modal */}
        <Dialog
          open={notesModalOpen}
          onClose={handleCloseNotesModal}
          maxWidth="sm"
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              width: { xs: '95%', sm: '80%', md: '60%' },
              m: { xs: 2, sm: 3 }
            }
          }}
        >
          <DialogTitle>
            Notes for {notesAutopayment?.payee || 'Autopayment'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Add reminders or notes about this automated payment.
            </DialogContentText>
            <TextField
              autoFocus
              multiline
              rows={4}
              fullWidth
              placeholder="Enter your notes here..."
              value={notesText}
              onChange={(e) => setNotesText(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseNotesModal}>Cancel</Button>
            <Button onClick={handleSaveNotes} variant="contained">
              Save Notes
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
}

export default AutopaymentsPage;
